import React, { useState, useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DateTime } from "luxon";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/Tabs"; // Adjust import if needed

// A helper to simulate your original method of converting a UTC time to business timezone.
const convertToBusinessTimezone = (utcTime, businessTimezone = "America/Toronto") => {
  // In this sample, we assume the times are already in UTC and convert to local zone
  // For real usage, you'd do a server call or keep the time in UTC for full accuracy.
  return DateTime.fromISO(utcTime, { zone: "UTC" })
    .setZone(businessTimezone)
    .toFormat("yyyy-MM-dd HH:mm");
};

// A helper that returns the next half hour in the given business timezone
const getNextHalfHourInBusinessTime = (businessTimezone = "UTC") => {
  const now = DateTime.now().setZone(businessTimezone);
  const minutes = now.minute;
  // Round up to the nearest half-hour
  const roundedMinutes = minutes + (30 - (minutes % 30)) % 30;
  return now.set({ minute: roundedMinutes, second: 0, millisecond: 0 }).toFormat("HH:mm");
};

const SampleDashboard = () => {
  // 1) Basic, local states to mimic user data, profile, etc.
  // No auth or token management here — everything is sample data.
  const [userEmail] = useState("demo@sampledashboard.com");

  // Pretend we fetched a profile from the server:
  const [profile, setProfile] = useState({
    name: "Sample Business",
    description: "Demo description for your sample dashboard page.",
    business_id: "samplebiz",
    business_address: "123 Demo Street, Example City",
    business_phone: "555-123-4567",
    business_email: "info@samplebiz.com",
    services: [
      { name: "Consultation", duration: 30 },
      { name: "Maintenance", duration: 60 }
    ],
    timezone: "America/Toronto",
    bookable_days: 8,
    booking_interval: 60,
    hours_advance: 0,
    published: false,
    req_cus_info: { name: true, email: true, phone: false, address: false },
    auto_confirm: true
  });

  // For displaying an array of local "service requests"
  const [serviceRequests, setServiceRequests] = useState([
    
  ]);

  // Local "events" for the calendar:
  const [events, setEvents] = useState([]);
  // Currently selected event (click from calendar)
  const [selectedEvent, setSelectedEvent] = useState(null);

  // For creating a new event in "My Calendar" tab
  const [newEvent, setNewEvent] = useState({
    title: "",
    event_date: DateTime.now().setZone(profile.timezone).toFormat("yyyy-MM-dd"),
    event_time: getNextHalfHourInBusinessTime(profile.timezone),
    duration: "30",
    customerName: "",
    customerAddress: "",
    customerEmail: "",
    customerPhone: "",
    services: ""
  });

  // UI toggles
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showCreateEventForm, setShowCreateEventForm] = useState(false);
  const [notifyOwner, setNotifyOwner] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(false);

  // Business hours, services, etc. — we'll store them locally
  const [workingHours, setWorkingHours] = useState([
    {
      day_of_week: "Monday",
      morning_start_time: "09:00",
      morning_end_time: "12:00",
      afternoon_start_time: "12:00",
      afternoon_end_time: "17:00",
      enabled: true
    },
    {
      day_of_week: "Tuesday",
      morning_start_time: "09:00",
      morning_end_time: "12:00",
      afternoon_start_time: "12:00",
      afternoon_end_time: "17:00",
      enabled: true
    },
    {
      day_of_week: "Wednesday",
      morning_start_time: "09:00",
      morning_end_time: "12:00",
      afternoon_start_time: "12:00",
      afternoon_end_time: "17:00",
      enabled: true
    },
    {
      day_of_week: "Thursday",
      morning_start_time: "09:00",
      morning_end_time: "12:00",
      afternoon_start_time: "12:00",
      afternoon_end_time: "17:00",
      enabled: true
    },
    {
      day_of_week: "Friday",
      morning_start_time: "09:00",
      morning_end_time: "12:00",
      afternoon_start_time: "12:00",
      afternoon_end_time: "17:00",
      enabled: true
    },
    {
      day_of_week: "Saturday",
      morning_start_time: "09:00",
      morning_end_time: "12:00",
      afternoon_start_time: "12:00",
      afternoon_end_time: "17:00",
      enabled: true
    },
    {
      day_of_week: "Sunday",
      morning_start_time: "09:00",
      morning_end_time: "12:00",
      afternoon_start_time: "12:00",
      afternoon_end_time: "17:00",
      enabled: true
    }
  ]);

  const [services, setServices] = useState(profile.services || []);
  const [newService, setNewService] = useState({ name: "", duration: "" });
  const [error, setError] = useState("");
  const [published, setPublished] = useState(profile.published);
  const [auto_confirm, setAuto_confirm] = useState(profile.auto_confirm);
  const [bookableDays, setBookableDays] = useState(profile.bookable_days);
  const [bookingInterval, setBookingInterval] = useState(profile.booking_interval);
  const [hoursAdvance, setHoursAdvance] = useState(profile.hours_advance);
  const [profileError, setProfileError] = useState("");
  const [reqCusInfo, setReqCusInfo] = useState(profile.req_cus_info);

  // Refs
  const selectedEventRef = useRef(null);
  const editEventRef = useRef(null);

  // Add these state variables and refs near your other state declarations
  const [showRiliCoins, setShowRiliCoins] = useState(false);
  const [showTier, setShowTier] = useState(false);

  const riliCoinsRef = useRef(null);
  const tierRef = useRef(null);
  const topSectionRiliCoinsRef = useRef(null);
  const topSectionTierRef = useRef(null);

  // Add this useEffect for handling clicks outside the account sections
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRiliCoins && riliCoinsRef.current && topSectionRiliCoinsRef.current && 
          !riliCoinsRef.current.contains(event.target) && !topSectionRiliCoinsRef.current.contains(event.target)) {
        setShowRiliCoins(false);
        setShowTier(false);
      }

      if (showTier && tierRef.current && topSectionTierRef.current && 
          !tierRef.current.contains(event.target) && !topSectionTierRef.current.contains(event.target)) {
        setShowTier(false);
        setShowRiliCoins(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showRiliCoins, showTier]);

  // 2) Initialize sample events on load
  useEffect(() => {
    // Just make some local events for the next 3 days
    const now = DateTime.now().setZone(profile.timezone || "UTC");
    const sampleEvents = [
      {
        id: 1,
        title: "Consultation - John",
        start_time: now.toISO(),
        end_time: now.plus({ minutes: 30 }).toISO()
      },
      {
        id: 2,
        title: "Maintenance - Jane",
        start_time: now.plus({ days: 1, hours: 1 }).toISO(),
        end_time: now.plus({ days: 1, hours: 2 }).toISO()
      }
    ];
    setEvents(sampleEvents);
  }, [profile.timezone]);

  // 3) Calendar event click
  const handleEventClick = (info) => {
    const eventId = parseInt(info.event.id, 10);
    const clickedEvent = events.find((evt) => evt.id === eventId);
    if (clickedEvent) {
      // Mock a deeper event record
      setSelectedEvent({
        ...clickedEvent,
        customer_name: "John Doe", // Hard-coded sample
        customer_address: "45 Example Rd, Cityville",
        customer_email: "john@example.com",
        customer_phone: "555-000-1111",
        services: "Consultation",
        additional_info: "Sample info here."
      });
      setTimeout(() => {
        if (editEventRef.current) {
          editEventRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  // 4) Create event
  const handleNewEventChange = (e) => {
    const { name, value } = e.target;
    setNewEvent((prev) => ({ ...prev, [name]: value }));
  };

  const createEvent = () => {
    try {
      setError("");
      // Convert date/time to an ISO string in the selected timezone
      const eventDateTime = DateTime.fromFormat(
        `${newEvent.event_date}T${newEvent.event_time}`,
        "yyyy-MM-dd'T'HH:mm",
        { zone: profile.timezone || "UTC" }
      );
      const start_time_utc = eventDateTime.toUTC().toISO();
      const end_time_utc = eventDateTime.plus({ minutes: parseInt(newEvent.duration, 10) }).toUTC().toISO();
      const newId = events.length ? Math.max(...events.map((evt) => evt.id)) + 1 : 1;

      const eventToAdd = {
        id: newId,
        title: newEvent.title || "New Event",
        start_time: start_time_utc,
        end_time: end_time_utc
      };

      setEvents((prev) => [...prev, eventToAdd]);
      alert("Event created successfully (demo only)!");
    } catch (err) {
      setError("Failed to create event (demo).");
    }
  };

  // 5) Delete event
  const deleteEvent = (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) {
      return;
    }
    setEvents((prev) => prev.filter((evt) => evt.id !== id));
    alert("Event deleted successfully (demo)!");
    setSelectedEvent(null);
  };

  // 6) Update selected event
  const updateEvent = (event) => {
    try {
      const updated = events.map((e) => (e.id === event.id ? { ...e, title: event.title } : e));
      setEvents(updated);
      alert("Event updated (demo)!");
      setSelectedEvent(null);
    } catch (err) {
      setError("Failed to update event (demo).");
    }
  };

  // 7) Accept/decline requests (demo stubs)
  const handleAcceptRequest = (id) => {
    alert(`Accepted request with ID=${id} (demo only)`);
    setServiceRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, status: "accepted" } : req))
    );
  };

  const handleDeclineRequest = (id) => {
    alert(`Declined request with ID=${id} (demo only)`);
    setServiceRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, status: "declined" } : req))
    );
  };

  // 8) Toggling business "publish" state (demo)
  const togglePublishStatus = () => {
    setPublished(!published);
    alert(`Your page is now ${!published ? "published" : "unpublished"} (demo).`);
  };

  // 9) Handling password change (demo)
  const handleChangePassword = () => {
    setPasswordError("Change password is disabled in demo.");
  };

  // For changing shift times, etc. (demo only)
  const updateShiftTime = (index, field, value) => {
    const updated = [...workingHours];
    updated[index][field] = value;
    setWorkingHours(updated);
  };

  // For day-of-week checkbox
  const setDayEnabled = (index, checked) => {
    const updated = [...workingHours];
    updated[index].enabled = checked;
    setWorkingHours(updated);
  };

  // For adding a new service
  const addService = () => {
    if (!newService.name || !newService.duration) {
      alert("Please provide both service name and duration.");
      return;
    }
    const serviceToAdd = {
      name: newService.name,
      duration: parseInt(newService.duration, 10)
    };
    setServices((prev) => [...prev, serviceToAdd]);
    setNewService({ name: "", duration: "" });
    alert("Service added (demo only).");
  };

  // For removing a service
  const removeService = (index) => {
    setServices((prev) => prev.filter((_, i) => i !== index));
    alert("Service removed (demo only).");
  };

  // 10) Sample for day labels
  const dayMap = {
    Monday: "MON",
    Tuesday: "TUE",
    Wednesday: "WED",
    Thursday: "THU",
    Friday: "FRI",
    Saturday: "SAT",
    Sunday: "SUN"
  };

  return (
    <div style={styles.container}>
      <p style={{ color: "green", fontWeight: "bold" }}>
            This is your dashboard to manage your calendar and appointments.<br /><br />
            Click the "Business Page" tab below to explore how to set up your business webpage.<br /><br />
          </p>
          <p style={{ color: "grey", fontSize: "12px" }}>
          Below is a demo page, data is not saved or sent anywhere.<br />
          Register to build your own page!
      </p>
      {/* Top Section */}
      <div style={styles.topSection}>
      
       
        <p style={styles.logo}>
          <a href="#" style={{ textDecoration: "none", color: "inherit" }}>
            Rili Day
          </a>
        </p>
        <p style={{ marginTop: "5px" }}>
          <a
            style={{
              textDecoration: "none",
              color: "grey",
              marginRight: "30px",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowRiliCoins((prevState) => !prevState);
              setShowTier(false);
            }}
            ref={topSectionRiliCoinsRef}
          >
            💰 {profile.rili_coins || 5}
          </a>
          <a
            style={{
              textDecoration: "none",
              color: "grey",
              marginLeft: "3px",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowTier((prevState) => !prevState);
              setShowRiliCoins(false);
            }}
            ref={topSectionTierRef}
          >
            👤 {profile.tier || "free"}
          </a>
        </p>
      </div>

      {/* Account Sections */}
      {showRiliCoins && (
        <div style={styles.accountSection} ref={riliCoinsRef}>
          <p>
            <strong>Rili Coins</strong>
            <br />
            <br />
            You may use your Rili Coins to upgrade your account to a premium tier once it is available.
            <br />
            <br />
            One Rili Coin can redeem for One Month of Premium Access.
            <br />
            <br />
            You can earn Rili Coins by inviting your friends to use Rili Day.
            <br />
            <br />
            Just share your referral code with your friends and when they sign up and <strong>accept the first booking</strong>, both of you will receive one Rili Coin.
            <br />
            <br />
            <strong>Your Referral Code:</strong> {profile.business_id || "samplebiz"}
          </p>
        </div>
      )}
      {showTier && (
        <div style={styles.accountSection} ref={tierRef}>
          <p>
            <strong>Free Tier</strong>
            <br />
            <br />
            Free tier includes all existing features plus lots of upcoming features.
            <br />
            <br />
            <strong>Upcoming Features for Free Tier:</strong>
            <br />
            - Basic customer management
            <br />
            - Sync with Google Calendar and Outlook
            <br />
            - Google Maps integration
            <br />
            - and more!
            <br />
            <br />
            <strong>Features in Future Premium Tier:</strong>
            <br />
            - Advanced customer management
            <br />
            - Invoice generation
            <br />
            - Deposit required booking
            <br />
            - SMS verification and notification
            <br />
            - and more!
          </p>
        </div>
      )}

      {/* Service Requests */}
      {serviceRequests.length > 0 && (
        <div style={styles.requestsSection}>
          <h2>Service Requests (Demo)</h2>
          {serviceRequests.map((request) => (
            <div key={request.id} style={styles.requestItem}>
              <p>
                <strong>Customer:</strong> {request.customer_name}
              </p>
              <p>
                <strong>Email:</strong> {request.customer_email}
              </p>
              <p>
                <strong>Phone:</strong> {request.customer_phone}
              </p>
              <p>
                <strong>Address:</strong> {request.customer_address}
              </p>
              <p>
                <strong>Service:</strong> {request.service_name}
              </p>
              <p>
                <strong>Duration:</strong> {request.service_duration} minutes
              </p>
              <p>
                <strong>Requested Time:</strong>{" "}
                {new Date(request.start_time).toLocaleString()}
              </p>
              <p>
                <strong>Additional Information:</strong> {request.additional_info}
              </p>
              <p>
                <strong>Status:</strong> {request.status}
              </p>
              <button onClick={() => handleAcceptRequest(request.id)} style={styles.acceptButton}>
                Accept
              </button>
              <button onClick={() => handleDeclineRequest(request.id)} style={styles.declineButton}>
                Decline
              </button>
            </div>
          ))}
        </div>
      )}

      {/* TABS */}
      <Tabs defaultValue="calendar">
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="calendar">My Calendar</TabsTrigger>
          <TabsTrigger value="setup">Business Page</TabsTrigger>
        </TabsList>

        {/* CALENDAR TAB */}
        <TabsContent value="calendar">
          <div style={styles.calendarSection}>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              height="auto"
              headerToolbar={{
                left: "prev,next today",
                center: "",
                right: "title"
              }}
              titleFormat={{ month: "long" }}
              nowIndicator={true}
              dayHeaderFormat={{ weekday: "narrow" }}
              eventClick={handleEventClick}
              // Convert our sample events to FullCalendar format
              events={events.map((evt) => ({
                id: evt.id,
                title: evt.title,
                start: DateTime.fromISO(evt.start_time, { zone: "UTC" })
                  .setZone(profile.timezone || "UTC")
                  .toISO(),
                end: DateTime.fromISO(evt.end_time, { zone: "UTC" })
                  .setZone(profile.timezone || "UTC")
                  .toISO(),
                backgroundColor: "rgba(14, 111, 1, 1)"
              }))}
            />
          </div>

          {/* EDIT SELECTED EVENT */}
          {selectedEvent && (
            <div
              ref={(el) => {
                selectedEventRef.current = el; // For click-outside detection
                editEventRef.current = el; // For scrolling
              }}
              style={styles.profileSection}
            >
              <h2 style={styles.sectionTitle}>View/Edit Selected Event (Demo)</h2>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                value={selectedEvent.title || ""}
                onChange={(e) => setSelectedEvent({ ...selectedEvent, title: e.target.value })}
                style={styles.input}
              />

              <label style={styles.label}>Start Time</label>
              <input
                type="datetime-local"
                value={
                  selectedEvent.start_time
                    ? DateTime.fromISO(selectedEvent.start_time, {
                        zone: profile.timezone || "UTC"
                      }).toFormat("yyyy-MM-dd'T'HH:mm")
                    : ""
                }
                onChange={(e) => {
                  const newStartTime = DateTime.fromFormat(
                    e.target.value,
                    "yyyy-MM-dd'T'HH:mm",
                    { zone: profile.timezone || "UTC" }
                  )
                    .toUTC()
                    .toISO();

                  // Keep the same duration
                  const durationMs =
                    DateTime.fromISO(selectedEvent.end_time).toMillis() -
                    DateTime.fromISO(selectedEvent.start_time).toMillis();

                  const newEndTime = DateTime.fromISO(newStartTime).plus({ milliseconds: durationMs }).toISO();

                  setSelectedEvent({
                    ...selectedEvent,
                    start_time: newStartTime,
                    end_time: newEndTime
                  });
                }}
                style={styles.input}
              />

              <label style={styles.label}>End Time</label>
              <input
                type="datetime-local"
                value={
                  selectedEvent.end_time
                    ? DateTime.fromISO(selectedEvent.end_time, {
                        zone: profile.timezone || "UTC"
                      }).toFormat("yyyy-MM-dd'T'HH:mm")
                    : ""
                }
                onChange={(e) =>
                  setSelectedEvent({
                    ...selectedEvent,
                    end_time: DateTime.fromFormat(e.target.value, "yyyy-MM-dd'T'HH:mm", {
                      zone: profile.timezone || "UTC"
                    })
                      .toUTC()
                      .toISO()
                  })
                }
                style={styles.input}
              />

              <label style={styles.label}>Customer Name</label>
              <input
                type="text"
                value={selectedEvent.customer_name || ""}
                onChange={(e) => setSelectedEvent({ ...selectedEvent, customer_name: e.target.value })}
                style={styles.input}
              />

              <label style={styles.label}>Customer Address</label>
              <input
                type="text"
                value={selectedEvent.customer_address || ""}
                onChange={(e) => setSelectedEvent({ ...selectedEvent, customer_address: e.target.value })}
                style={styles.input}
              />

              <label style={styles.label}>Customer Email</label>
              <input
                type="email"
                value={selectedEvent.customer_email || ""}
                onChange={(e) => setSelectedEvent({ ...selectedEvent, customer_email: e.target.value })}
                style={styles.input}
              />

              <label style={styles.label}>Customer Phone</label>
              <input
                type="tel"
                value={selectedEvent.customer_phone || ""}
                onChange={(e) =>
                  setSelectedEvent({ ...selectedEvent, customer_phone: e.target.value.replace(/\D/g, "") })
                }
                style={styles.input}
              />

              <label style={styles.label}>Services</label>
              <input
                type="text"
                value={selectedEvent.services || ""}
                onChange={(e) => setSelectedEvent({ ...selectedEvent, services: e.target.value })}
                style={styles.input}
              />

              <label style={styles.label}>Additional Information</label>
              <textarea
                style={styles.input}
                value={selectedEvent.additional_info || ""}
                onChange={(e) =>
                  setSelectedEvent({ ...selectedEvent, additional_info: e.target.value })
                }
              />
              <button onClick={() => updateEvent(selectedEvent)}>
                Save Changes
              </button>

              <button onClick={() => setSelectedEvent(null)}>
                Cancel
              </button>

              <button onClick={() => deleteEvent(selectedEvent.id)}>Delete Event</button>
            </div>
          )}

          {/* CREATE NEW EVENT FORM */}
          <button onClick={() => setShowCreateEventForm((prev) => !prev)} style={styles.button}>
            {showCreateEventForm ? "Cancel" : "Create New Event"}
          </button>
          {showCreateEventForm && (
            <div style={styles.profileSection}>
              <label style={styles.label}>Event Title</label>
              <input
                type="text"
                name="title"
                placeholder="Event Title"
                value={newEvent.title}
                onChange={handleNewEventChange}
                style={styles.input}
              />

              <label style={styles.label}>Event Date</label>
              <input
                type="date"
                name="event_date"
                value={newEvent.event_date}
                onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                style={styles.input}
              />

              <label style={styles.label}>Start Time</label>
              <input
                type="time"
                name="event_time"
                value={newEvent.event_time}
                onChange={handleNewEventChange}
                style={styles.input}
              />

              <label style={styles.label}>Duration (minutes)</label>
              <input
                type="number"
                name="duration"
                value={newEvent.duration}
                onChange={handleNewEventChange}
                style={styles.input}
              />

              <input
                type="text"
                name="customerName"
                placeholder="Customer Name"
                value={newEvent.customerName}
                onChange={handleNewEventChange}
                style={styles.input}
              />
              <input
                type="text"
                name="customerAddress"
                placeholder="Customer Address"
                value={newEvent.customerAddress}
                onChange={handleNewEventChange}
                style={styles.input}
              />
              <input
                type="email"
                name="customerEmail"
                placeholder="Customer Email"
                value={newEvent.customerEmail}
                onChange={handleNewEventChange}
                style={styles.input}
              />
              <input
                type="tel"
                name="customerPhone"
                placeholder="Customer Phone"
                value={newEvent.customerPhone}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    customerPhone: e.target.value.replace(/\D/g, "")
                  })
                }
                style={styles.input}
              />
              <input
                type="text"
                name="services"
                placeholder="Services Required"
                value={newEvent.services}
                onChange={handleNewEventChange}
                style={styles.input}
              />
              <button onClick={createEvent} style={styles.button}>
                Create Event
              </button>
            </div>
          )}
        </TabsContent>

        {/* BUSINESS PAGE TAB */}
        <TabsContent value="setup">
          {/* Business Profile */}
            
          

          <div style={styles.profileSection}>
          {published ? (
              <>
                <p style={{ color: "green", fontWeight: "bold" }}>✅ Your demo page is published! 
                  <br />Register to create your own page for FREE!</p>
                <p>
                  Your public page is:
                  <br />
                  <a
                    href={`/samplebiz`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {window.location.origin}/samplebiz
                  </a>
                </p>
              </>
            ) : (
              <p style={{ color: "red", fontWeight: "bold" }}>
                Complete your business info and publish at the end of this page (demo).
              </p>
            )}
            <h2 style={styles.sectionTitle}>Business Profile</h2>

            <label style={styles.label}>Business Name</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              style={styles.input}
            />

            <label style={styles.label}>Business Description</label>
            <textarea
              style={styles.textarea}
              value={profile.description}
              onChange={(e) => setProfile({ ...profile, description: e.target.value })}
            />

            <label style={styles.label}>Business Email</label>
            <input
              type="text"
              value={profile.business_email}
              onChange={(e) => setProfile({ ...profile, business_email: e.target.value })}
              style={styles.input}
            />

            <label style={styles.label}>Business Address</label>
            <input
              type="text"
              value={profile.business_address}
              onChange={(e) => setProfile({ ...profile, business_address: e.target.value })}
              style={styles.input}
            />

            <label style={styles.label}>Business Phone</label>
            <input
              type="tel"
              value={profile.business_phone}
              onChange={(e) => setProfile({ ...profile, business_phone: e.target.value })}
              style={styles.input}
            />

            <div style={styles.inputGroup}>
                                <label style={styles.label}>Online Meeting Link</label>
                                <p style={{ fontSize: '12px', color: '#555', marginTop: '0px' }}>
                                Once set, this link will be included in your booking confirmation email by default.
                                <br />
                                If you want to have a different link for a specific event, 
                                <br />
                                you can update it by clicking the "Edit" button for that event.
                                </p>
                                <input
                                    type="text"
                                    placeholder="Optional"
                                    value={profile.ol_link}
                                    onChange={(e) => setProfile({ ...profile, ol_link: e.target.value })}
                                    style={styles.input}
                                />
                                
            </div>

            <button
              onClick={() => alert("Profile saved (demo only).")}
              style={styles.button}
            >
              Save Profile
            </button>
            {profileError && <p style={styles.error}>{profileError}</p>}
          </div>

          {/* Working Hours */}
          <div style={styles.workingHoursSection}>
            <h2 style={styles.sectionTitle}>Set Business Hours</h2>
            <div style={styles.workingHoursRow}>
              <div style={styles.dayCheckboxCol}></div>
              <div style={styles.shiftGroup}>
                <p>Morning Shift</p>
              </div>
              <div style={styles.shiftGroup}>
                <p>Afternoon Shift</p>
              </div>
            </div>

            {workingHours.map((day, index) => (
              <div key={index} style={styles.workingHoursRow}>
                <div style={styles.dayCheckboxCol}>
                  <input
                    type="checkbox"
                    checked={day.enabled}
                    onChange={(e) => setDayEnabled(index, e.target.checked)}
                  />
                  {dayMap[day.day_of_week]}
                </div>
                <div style={styles.shiftGroup}>
                  <input
                    style={styles.timeInput}
                    type="time"
                    disabled={!day.enabled}
                    value={day.morning_start_time}
                    onChange={(e) => updateShiftTime(index, "morning_start_time", e.target.value)}
                  />
                  <input
                    style={styles.timeInput}
                    type="time"
                    disabled={!day.enabled}
                    value={day.morning_end_time}
                    onChange={(e) => updateShiftTime(index, "morning_end_time", e.target.value)}
                  />
                </div>
                <div style={styles.shiftGroup}>
                  <input
                    style={styles.timeInput}
                    type="time"
                    disabled={!day.enabled}
                    value={day.afternoon_start_time}
                    onChange={(e) => updateShiftTime(index, "afternoon_start_time", e.target.value)}
                  />
                  <input
                    style={styles.timeInput}
                    type="time"
                    disabled={!day.enabled}
                    value={day.afternoon_end_time}
                    onChange={(e) => updateShiftTime(index, "afternoon_end_time", e.target.value)}
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => alert("Business hours saved (demo).")}
              style={styles.button}
            >
              Save Business Hours
            </button>
          </div>

          {/* Services */}
          <div style={styles.servicesSection}>
            <h2 style={styles.sectionTitle}>Set Available Services</h2>
            <label style={styles.label}>Service Name</label>
            <input
              type="text"
              placeholder="Enter service name"
              value={newService.name}
              onChange={(e) => setNewService({ ...newService, name: e.target.value })}
              style={styles.input}
            />
            <label style={styles.label}>Duration (minutes)</label>
            <input
              type="number"
              placeholder="Enter duration"
              value={newService.duration}
              onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
              style={styles.input}
            />
            <button onClick={addService} style={styles.button}>
              Add Service
            </button>

            {services.length > 0 ? (
              <div style={styles.servicesList}>
                {services.map((service, index) => (
                  <div key={index} style={styles.serviceItem}>
                    <span>
                      {service.name} - {service.duration} minutes
                    </span>
                    <button onClick={() => removeService(index)} style={styles.deleteButton}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p>No services available. Add a new one!</p>
            )}
          </div>

          {/* Booking Options */}
          <div style={styles.profileSection}>
            <h2>Configure Your Booking Options</h2>
            <div style={styles.inputGroup}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <label style={{ ...styles.label}}>Allow Auto-Confirmation</label>
                                    <input 
                                        type="checkbox" 
                                        checked={auto_confirm} 
                                        onChange={(e) => setAuto_confirm(e.target.checked)}
                                        style={{ width: '40px', height: '20px', marginTop: '-2px' }}
                                    />
                                </div>
                                <p style={{ fontSize: '12px', color: '#555', marginTop: '0px' }}>
                                    All valid service requests will be accepted automatically without your manual approval. 
                                    <br />
                                    To enable instant confirmation, please maintain an accurate calendar.
                                </p>
            </div>

                            <div style={styles.inputGroup}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                <label style={{...styles.label}}>Send Event Reminders to me</label>
                                <input 
                                    type="checkbox" 
                                    checked={notifyOwner} 
                                    onChange={(e) => setNotifyOwner(e.target.checked)}
                                    style={{ width: '40px', height: '20px', marginTop: '-2px' }}
                                />
                                </div>
                                <p style={{ fontSize: '12px', color: '#555', marginTop: '0px' }}>
                                    You will receive email reminders 24 hours and 2 hours before the event.
                                </p>
                            </div>

                            <div style={styles.inputGroup}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                <label style={{...styles.label, marginRight: '10px'}}>Send Event Reminders to my customers</label>
                                <input 
                                    type="checkbox" 
                                    checked={notifyCustomer} 
                                    onChange={(e) => setNotifyCustomer(e.target.checked)}
                                    style={{ width: '40px', height: '20px', marginTop: '-2px' }}
                                />
                                </div>
                                <p style={{ fontSize: '12px', color: '#555', marginTop: '0px' }}>
                                    Your customers will receive email reminders 24 hours and 2 hours before the event.
                                    <br />
                                    Only customers with a valid email address will receive reminders.
                                </p>
                            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Show availability for the next</label>
              <select
                value={bookableDays}
                onChange={(e) => setBookableDays(Number(e.target.value))}
                style={styles.input}
              >
                {[...Array(90)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} days
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>
                Accept available slots start at what time increments?
              </label>
              <p style={{ fontSize: "12px", color: "#555", marginTop: "0px" }}>
                E.g.: <br />
                (15 min) 9:00, 9:15, 9:30, 9:45 <br />
                (30 min) 9:00, 9:30 <br />
                (60 min) 9:00
              </p>
              <select
                value={bookingInterval}
                onChange={(e) => setBookingInterval(Number(e.target.value))}
                style={styles.input}
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Customer must book at least</label>
              <select
                value={hoursAdvance}
                onChange={(e) => setHoursAdvance(Number(e.target.value))}
                style={styles.input}
              >
                {[...Array(49)].map((_, i) => (
                  <option key={i} value={i}>
                    {i} hours in advance
                  </option>
                ))}
              </select>
            </div>

            <label style={styles.label}>Required customer information</label>
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={reqCusInfo.name}
                  onChange={(e) => setReqCusInfo({ ...reqCusInfo, name: e.target.checked })}
                />
                Name
              </label>
            </div>
            <div>
              <label>
                <input type="checkbox" checked={true} disabled /> Email (Always Required)
              </label>
            </div>
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={reqCusInfo.phone}
                  onChange={(e) => setReqCusInfo({ ...reqCusInfo, phone: e.target.checked })}
                />
                Phone
              </label>
            </div>
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={reqCusInfo.address}
                  onChange={(e) => setReqCusInfo({ ...reqCusInfo, address: e.target.checked })}
                />
                Address
              </label>
            </div>
            <button
              onClick={() => alert("Booking options saved (demo).")}
              style={styles.button}
            >
              Save Booking Options
            </button>
          </div>

          {/* Publish Section */}
          <div style={styles.profileSection}>
            <h2>Publish Your Business Page (Demo)</h2>
            <label style={styles.label}>Business ID</label>
            <p style={{ fontSize: "14px", color: "#555" }}>
              This will be added to your public page URL (demo). Only letters & numbers allowed.
            </p>
            <input
              type="text"
              placeholder="Enter a unique business ID"
              value={profile.business_id}
              onChange={(e) => {
                const value = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "");
                setProfile({ ...profile, business_id: value });
              }}
              style={styles.input}
            />
            <button onClick={() => alert("Business ID saved (demo).")} style={styles.button}>
              Save Business ID
            </button>

            {profile.business_id && profile.name ? (
              <>
                {published ? (
                  <>
                    <p style={{ color: "green", fontWeight: "bold" }}>✅ Your demo page is published!
                    <br />Register to create your own page for FREE!
                    </p>
                    <p>
                      Your public page is:
                      <br />
                      <a href={`/samplebiz`} target="_blank" rel="noopener noreferrer">
                        {window.location.origin}/samplebiz
                      </a>
                    </p>
                  </>
                ) : (
                  <p style={{ color: "red", fontWeight: "bold" }}>
                    ⚠️ Your page is not published yet (demo). <br />
                    Try click the "Publish" button!
                  </p>
                )}
                <button onClick={togglePublishStatus} style={styles.button}>
                  {published ? "Unpublish Page" : "Publish Page"}
                </button>
              </>
            ) : (
              <p style={{ color: "red", fontWeight: "bold" }}>
                ⚠️ Business ID and Business Name required before publishing (demo).
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Error display */}
      {error && <p style={styles.error}>{error}</p>}
    </div>
  );
};

// STYLES
const styles = {
  container: {
    position: "relative",
    padding: "20px",
    paddingTop: "0px",
    maxWidth: "800px",
    margin: "0 auto",
    fontFamily: "Arial, sans-serif",
    lineHeight: "1.6"
  },
  bottomSection: {
    marginBottom: "20px",
    marginTop: "100px",
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    alignItems: "flex-start"
  },
  topSection: {
    display: "flex",
    justifyContent: "space-between",
    paddingBottom: "20px",
    paddingTop: "10px",
    maxWidth: "800px",
    margin: "0 auto",
    lineHeight: "1.6"
  },
  logo: {
    fontFamily: "Verdana, sans-serif",
    fontWeight: "bold",
    fontSize: "24px",
    color: "#2c3e50",
    margin: "0px"
  },
  button: {
    padding: "10px 20px",
    backgroundColor: "#2c3e50",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginTop: "10px",
    transition: "background-color 0.3s ease"
  },
  passwordButton: {
    padding: "10px 20px",
    backgroundColor: "grey",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginTop: "10px",
    transition: "background-color 0.3s ease"
  },
  logoutButton: {
    padding: "10px 20px",
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginTop: "10px",
    transition: "background-color 0.3s ease"
  },
  profileSection: {
    marginTop: "0px",
    marginBottom: "30px",
    padding: "20px",
    borderRadius: "0px",
    backgroundColor: "#f9f9f9"
  },
  sectionTitle: {
    fontSize: "24px",
    marginBottom: "15px"
  },
  label: {
    display: "block",
    marginBottom: "5px",
    fontWeight: "bold"
  },
  input: {
    width: "80%",
    padding: "10px",
    borderRadius: "5px",
    border: "0px solid #ccc",
    fontSize: "16px",
    display: "block",
    marginTop: "5px",
    marginBottom: "10px"
  },
  textarea: {
    width: "80%",
    padding: "10px",
    borderRadius: "5px",
    border: "0px solid #ccc",
    fontSize: "16px",
    height: "80px",
    marginTop: "5px",
    marginBottom: "10px"
  },
  error: {
    color: "red",
    marginBottom: "10px"
  },
  requestsSection: {
    marginTop: "30px",
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9"
  },
  requestItem: {
    marginBottom: "20px",
    padding: "15px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)"
  },
  acceptButton: {
    marginRight: "10px",
    backgroundColor: "#28a745",
    color: "#fff",
    padding: "5px 10px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    margin: "10px 5px 0 0"
  },
  declineButton: {
    backgroundColor: "#dc3545",
    color: "#fff",
    padding: "5px 10px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    margin: "10px 0 0 0"
  },
  calendarSection: {
    textAlign: "left",
    marginTop: "0px",
    marginBottom: "10px",
    paddingBottom: "20px",
    paddingTop: "20px",
    backgroundColor: "#f9f9f9"
  },
  saveButton: {
    backgroundColor: "#28a745",
    color: "#fff",
    marginRight: "10px",
    border: "none",
    borderRadius: "5px",
    padding: "10px"
  },
  cancelButton: {
    backgroundColor: "grey",
    color: "#fff",
    marginRight: "10px",
    border: "none",
    borderRadius: "5px",
    padding: "10px"
  },
  workingHoursSection: {
    marginTop: "30px",
    padding: "20px",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9"
  },
  workingHoursRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    marginBottom: "15px",
    justifyContent: "space-between",
    gap: "10px"
  },
  shiftGroup: {
    width: "30%",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    flex: "1"
  },
  timeInput: {
    width: "auto",
    fontSize: "16px",
    borderRadius: "5px",
    border: "0px solid #ccc"
  },
  dayCheckboxCol: {
    width: "20%",
    position: "relative",
    top: "0"
  },
  servicesSection: {
    marginTop: "30px",
    padding: "20px",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9"
  },
  servicesList: {
    marginTop: "20px",
    padding: "10px",
    backgroundColor: "#fff",
    borderRadius: "5px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
  },
  serviceItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px"
  },
  deleteButton: {
    padding: "10px 20px",
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginTop: "10px"
  },
  accountSection: {
    position: "absolute",
    zIndex: 1000,
    right: "0",
    marginTop: "-30px",
    maxWidth: "240px",
    padding: "5px 20px 10px 20px",
    borderRadius: "8px",
    backgroundColor: "#e0e0e0",
    marginBottom: "20px",
    fontSize: "11px",
    textAlign: "left",
  },
};

export default SampleDashboard;
